import { reconcileBacktestDisplayMetrics } from '../../utils/backtestReturnReconcile';
import moment from 'moment';

export const BACKTEST_REPLAY_DURATION_MS = 10000;
export const REPLAY_X_AXIS_TICKS = [0, 25, 50, 75, 100];
export const DEFAULT_RUNNING_START_VALUE = 100000;

export const toFiniteNumber = (value) => {
  if (value === null || value === undefined || value === '') return null;
  const cleaned = typeof value === 'string' ? value.replace(/[^0-9.-]/g, '') : value;
  const parsed = Number(cleaned);
  return Number.isFinite(parsed) ? parsed : null;
};

export const firstFiniteNumber = (values) => {
  for (const value of values) {
    const numeric = toFiniteNumber(value);
    if (numeric !== null) return numeric;
  }
  return null;
};

export const parseChartDate = (value) => {
  if (!value) return null;
  const parsed = moment(value, ['YYYY-MM-DD HH:mm:ss', 'YYYY-MM-DD', moment.ISO_8601]);
  return parsed.isValid() ? parsed : null;
};

export const formatSummaryDate = (value) => {
  const parsed = parseChartDate(value);
  return parsed ? parsed.format('MMM D, YYYY') : null;
};

export const formatCurrencyValue = (value) => {
  const numeric = toFiniteNumber(value);
  if (numeric === null) return null;
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 0,
  }).format(numeric);
};

export const formatSignedCurrencyValue = (value) => {
  const numeric = toFiniteNumber(value);
  if (numeric === null) return null;
  const sign = numeric >= 0 ? '+' : '-';
  return `${sign}${formatCurrencyValue(Math.abs(numeric))}`;
};

export const formatPercentValue = (value, { signed = true } = {}) => {
  const numeric = toFiniteNumber(value);
  if (numeric === null) return null;
  const pct = numeric * 100;
  const sign = signed && pct >= 0 ? '+' : pct < 0 ? '-' : '';
  const formatted = Math.abs(pct).toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
  return `${sign}${formatted}%`;
};

export const normalizeEquityPoints = (points = []) =>
  points
    .map((point, index) => {
      const value = firstFiniteNumber([point?.y, point?.value, point?.portfolioValue]);
      if (value === null) return null;
      return {
        ...point,
        index,
        date: point?.date || point?.time || point?.simulation_date,
        y: value,
      };
    })
    .filter(Boolean);

export const getAxisDateGranularity = (points = []) => {
  const validMoments = points
    .map((point) => parseChartDate(point?.date ?? point?.chartX))
    .filter(Boolean);

  if (validMoments.length < 2) return 'day';

  const startMoment = validMoments[0];
  const endMoment = validMoments[validMoments.length - 1];
  const spanDays = Math.abs(endMoment.diff(startMoment, 'days'));

  if (spanDays <= 45) return 'day';
  if (spanDays <= 540) return 'month';
  return 'year';
};

export const formatAxisDate = (value, granularity) => {
  const parsed = parseChartDate(value);
  if (!parsed) return '';

  if (granularity === 'year') return parsed.format('YYYY');
  if (granularity === 'month') return parsed.format("MMM 'YY");
  return parsed.format('MMM D');
};

export const calculateEquityMetrics = (points = []) => {
  const validPoints = points.filter((point) => toFiniteNumber(point?.y) !== null);
  if (validPoints.length < 2) return null;

  const startValue = toFiniteNumber(validPoints[0].y);
  const endValue = toFiniteNumber(validPoints[validPoints.length - 1].y);
  if (!startValue || startValue <= 0 || endValue === null) return null;

  let peak = startValue;
  let maxDrawdown = 0;

  validPoints.forEach((point) => {
    const value = toFiniteNumber(point.y);
    if (value === null) return;
    peak = Math.max(peak, value);
    if (peak > 0) {
      maxDrawdown = Math.min(maxDrawdown, value / peak - 1);
    }
  });

  const totalReturn = endValue / startValue - 1;
  const startMoment = parseChartDate(validPoints[0].date);
  const endMoment = parseChartDate(validPoints[validPoints.length - 1].date);
  const elapsedYears =
    startMoment && endMoment ? Math.max(endMoment.diff(startMoment, 'days') / 365.25, 0) : 0;
  const annualReturn =
    elapsedYears > 0.05 ? Math.pow(endValue / startValue, 1 / elapsedYears) - 1 : totalReturn;

  return {
    totalReturn,
    maxDrawdown,
    annualReturn,
  };
};

const getPointAtReplayTick = (tick, points) => {
  if (!points || points.length === 0) return null;
  if (points.length === 1) return points[0];

  const normalizedTick = Math.max(0, Math.min(100, Number(tick)));
  if (!Number.isFinite(normalizedTick)) return null;

  const tickIndex = Math.round((normalizedTick / 100) * (points.length - 1));
  return points[Math.max(0, Math.min(points.length - 1, tickIndex))];
};

const getChartDomain = (points = []) => {
  const values = points.map((point) => toFiniteNumber(point?.y)).filter((value) => value !== null);
  if (values.length === 0) return [0, 100];
  const min = values.reduce((a, b) => Math.min(a, b), Infinity);
  const max = values.reduce((a, b) => Math.max(a, b), -Infinity);
  const padding = (max - min) * 0.12 || Math.max(Math.abs(max) * 0.08, 1);
  return [min - padding, max + padding];
};

const getReturnDomain = (points = []) => {
  const values = points
    .flatMap((point) => [
      toFiniteNumber(point?.returnPct),
      toFiniteNumber(point?.benchmarkReturnPct),
    ])
    .filter((value) => value !== null);
  if (values.length === 0) return [-0.01, 0.01];

  const min = Math.min(0, ...values);
  const max = Math.max(0, ...values);
  const padding = (max - min) * 0.12 || 0.01;
  return [min - padding, max + padding];
};

const getElapsedDays = (start, end) => {
  const startMoment = parseChartDate(start);
  const endMoment = parseChartDate(end);
  if (!startMoment || !endMoment) return null;
  return Math.max(0, endMoment.diff(startMoment, 'days'));
};

export const createBacktestEquityViewModel = ({
  mode = 'completed',
  points = [],
  result = {},
  progress = null,
  replayProgress = 1,
  metrics = null,
} = {}) => {
  const normalizedPoints = normalizeEquityPoints(points);
  const isPlayback = mode === 'replay' || mode === 'running';
  const progressValue = firstFiniteNumber([progress?.portfolio_value]);
  const progressPercentRaw = firstFiniteNumber([progress?.percent, progress?.pct]);
  const progressPercent =
    progressPercentRaw === null ? null : Math.max(0, Math.min(100, progressPercentRaw));
  const rawFirstPoint = normalizedPoints[0] || null;

  const startValue = firstFiniteNumber([
    rawFirstPoint?.y,
    result?.initialPortfolioValue,
    result?.initialValue,
    result?.initialCash,
    result?.startingCash,
    result?.startingBalance,
    result?.backtestingBudget,
    result?.backtestBudget,
    result?.budget,
    result?.config?.BACKTESTING_BUDGET,
    result?.env?.BACKTESTING_BUDGET,
    result?.parameters?.budget,
    mode === 'running' ? progressValue : null,
    mode === 'running' ? DEFAULT_RUNNING_START_VALUE : null,
  ]);

  const baselineDate =
    progress?.simulation_date ||
    result?.startDate ||
    result?.startTime ||
    result?.endDate ||
    result?.endTime ||
    null;
  const baselineEndValue = firstFiniteNumber([progressValue, startValue]);
  const modelPoints =
    normalizedPoints.length > 0
      ? normalizedPoints
      : startValue !== null
        ? [
            { x: 0, date: baselineDate, y: startValue, isBaseline: true },
            {
              x: mode === 'running' ? (progressPercent ?? 0) : 100,
              date: baselineDate,
              y: baselineEndValue,
              isBaseline: true,
            },
          ]
        : [];

  const visibleIndex =
    mode === 'replay' && modelPoints.length > 0
      ? Math.max(
          1,
          Math.min(modelPoints.length - 1, Math.floor(replayProgress * (modelPoints.length - 1))),
        )
      : modelPoints.length - 1;

  const visiblePoints = mode === 'replay' ? modelPoints.slice(0, visibleIndex + 1) : modelPoints;

  const firstPoint = visiblePoints[0] || modelPoints[0] || null;
  const lastVisiblePoint = visiblePoints[visiblePoints.length - 1] || null;
  const lastPoint = modelPoints[modelPoints.length - 1] || null;

  const currentValue =
    mode === 'running'
      ? firstFiniteNumber([progressValue, lastVisiblePoint?.y])
      : firstFiniteNumber([lastVisiblePoint?.y]);

  const finalValue = firstFiniteNumber([
    mode === 'completed' ? lastPoint?.y : null,
    result?.finalPortfolioValue,
    result?.finalValue,
    result?.endingValue,
    result?.finalEquity,
    result?.accountValue,
  ]);

  const displayEndValue = mode === 'completed' ? finalValue : currentValue;
  const gainLoss =
    startValue !== null && displayEndValue !== null ? displayEndValue - startValue : null;
  const gainLossPct =
    startValue && startValue > 0 && gainLoss !== null ? gainLoss / startValue : null;
  const trendDirection = gainLoss === null ? 'neutral' : gainLoss >= 0 ? 'positive' : 'negative';

  const maxIndex = Math.max(visiblePoints.length - 1, 1);
  const benchmarkStartValue = firstFiniteNumber(visiblePoints.map((point) => point?.benchmark));
  const chartData = visiblePoints.map((point, index) => ({
    ...point,
    returnPct:
      startValue && startValue > 0 ? (toFiniteNumber(point?.y) - startValue) / startValue : null,
    benchmarkReturnPct:
      benchmarkStartValue && benchmarkStartValue > 0 && toFiniteNumber(point?.benchmark) !== null
        ? (toFiniteNumber(point.benchmark) - benchmarkStartValue) / benchmarkStartValue
        : null,
    chartX:
      mode === 'running'
        ? (firstFiniteNumber([point?.x]) ?? (index / maxIndex) * (progressPercent ?? 100))
        : mode === 'replay'
          ? (index / maxIndex) * 100
          : point.date,
    currentY: isPlayback && index === visiblePoints.length - 1 ? point.y : null,
  }));

  const axisDateGranularity = getAxisDateGranularity(chartData);
  const calculatedMetrics = calculateEquityMetrics(visiblePoints);
  const hasBenchmark = chartData.some((point) => toFiniteNumber(point?.benchmark) !== null);

  const reconciledMetrics = reconcileBacktestDisplayMetrics({
    apiMetrics: metrics,
    equityMetrics: calculatedMetrics,
    startValue,
    endValue: displayEndValue,
  });

  return {
    mode,
    isPlayback,
    progressPercent,
    chartData,
    hasBenchmark,
    visiblePoints,
    chartDomain: getChartDomain(chartData),
    chartReturnDomain: getReturnDomain(chartData),
    axisDateGranularity,
    startDate: formatSummaryDate(result?.startDate || result?.startTime || firstPoint?.date),
    endDate: formatSummaryDate(
      mode === 'completed'
        ? result?.endDate || result?.endTime || lastPoint?.date
        : progress?.simulation_date || lastVisiblePoint?.date,
    ),
    elapsedDays: getElapsedDays(
      result?.startDate || result?.startTime || firstPoint?.date,
      mode === 'completed'
        ? result?.endDate || result?.endTime || lastPoint?.date
        : progress?.simulation_date || lastVisiblePoint?.date,
    ),
    startValue,
    currentValue,
    finalValue: displayEndValue,
    gainLoss,
    gainLossPct,
    trendDirection,
    // Completed runs: prefer balance-based Total Return so MetricsRow matches
    // the start→end account balance shown above the chart.
    metrics:
      mode === 'completed'
        ? reconciledMetrics.totalReturn != null || calculatedMetrics || metrics
          ? reconciledMetrics
          : null
        : calculatedMetrics || metrics,
    getReplayTickLabel: (tick) => {
      const tickPoint = getPointAtReplayTick(tick, chartData);
      return formatAxisDate(tickPoint?.date, axisDateGranularity);
    },
  };
};
